/**
 * SpacePopover — context menu for a single space pill (⋯ affordance).
 *
 * Shows: Rename / Duplicate / Delete (with two-step confirm, matching TileCell's
 * remove pattern — D-016 §2.5 says "two-step stays"). Delete is hidden when the
 * space is the last one (last-space guard). Auto-dismisses on outside click or
 * Escape. Positioned below the pill that triggered it.
 *
 * A11y: role="menu", arrow-key navigation, Enter selects, Escape closes.
 * iOS design language: rounded popover, subtle shadow, 44px touch targets.
 * Reduced-motion: no entry animation.
 *
 * Immutability: pure controlled component.
 */
import { useEffect, useRef, useState } from 'react';

export interface SpacePopoverProps {
  readonly spaceId: string;
  readonly spaceName: string;
  /** Is this the only space? If so, Delete is suppressed. */
  readonly isLast: boolean;
  /** Anchor element (the ⋯ button) for positioning. */
  readonly anchorRect: DOMRect;
  readonly onRename: () => void;
  readonly onDuplicate: () => void;
  readonly onDelete: () => void;
  readonly onClose: () => void;
}

/** Detect prefers-reduced-motion. */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function SpacePopover({
  spaceName,
  isLast,
  anchorRect,
  onRename,
  onDuplicate,
  onDelete,
  onClose,
}: SpacePopoverProps): React.ReactElement {
  const [deleteStep, setDeleteStep] = useState<'idle' | 'confirm'>('idle');
  const popoverRef = useRef<HTMLDivElement>(null);
  const reduced = prefersReducedMotion();

  // Close on outside click.
  useEffect(() => {
    function handleClick(e: MouseEvent): void {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => { document.removeEventListener('mousedown', handleClick); };
  }, [onClose]);

  // Escape closes.
  useEffect(() => {
    function handleKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener('keydown', handleKey, true);
    return () => { document.removeEventListener('keydown', handleKey, true); };
  }, [onClose]);

  // Focus first item on open.
  useEffect(() => {
    const first = popoverRef.current?.querySelector<HTMLElement>('[role="menuitem"]');
    first?.focus();
  }, []);

  // MEDIUM-2: Revert delete-confirm step to 'idle' after ~3s.
  useEffect(() => {
    if (deleteStep !== 'confirm') return;
    const timer = setTimeout(() => { setDeleteStep('idle'); }, 3000);
    return () => { clearTimeout(timer); };
  }, [deleteStep]);

  // Arrow-key navigation within the menu.
  function handleMenuKeyDown(e: React.KeyboardEvent): void {
    const items = Array.from(
      popoverRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
    );
    const idx = items.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      items[(idx + 1) % items.length]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      items[(idx - 1 + items.length) % items.length]?.focus();
    }
  }

  // Position: below the anchor, centered.
  const top = anchorRect.bottom + 6;
  const left = anchorRect.left + anchorRect.width / 2;

  function handleDelete(): void {
    if (deleteStep === 'idle') {
      setDeleteStep('confirm');
    } else {
      onDelete();
      onClose();
    }
  }

  const itemStyle: React.CSSProperties = {
    display: 'block',
    width: '100%',
    minHeight: '44px',
    padding: '0 16px',
    textAlign: 'left',
    background: 'none',
    border: 'none',
    borderRadius: '8px',
    fontSize: 'var(--text-delta)',
    color: 'var(--color-text-primary)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  };

  const deleteStyle: React.CSSProperties = {
    ...itemStyle,
    color: deleteStep === 'confirm' ? '#fff' : 'var(--color-negative-accent)',
    backgroundColor: deleteStep === 'confirm' ? 'var(--color-negative-accent)' : 'transparent',
  };

  return (
    <div
      ref={popoverRef}
      role="menu"
      aria-label={`Options for ${spaceName}`}
      onKeyDown={handleMenuKeyDown}
      style={{
        position: 'fixed',
        top,
        left,
        transform: 'translateX(-50%)',
        zIndex: 700,
        backgroundColor: 'var(--color-bg-tile)',
        borderRadius: '14px',
        padding: '6px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
        border: '1px solid var(--color-border)',
        animation: reduced ? undefined : 'spacePopoverIn 140ms ease-out both',
        minWidth: '160px',
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
      }}
    >
      <button
        type="button"
        role="menuitem"
        onClick={() => { onRename(); onClose(); }}
        style={itemStyle}
      >
        Rename
      </button>
      <button
        type="button"
        role="menuitem"
        onClick={() => { onDuplicate(); onClose(); }}
        style={itemStyle}
      >
        Duplicate
      </button>
      {!isLast && (
        <button
          type="button"
          role="menuitem"
          onClick={handleDelete}
          aria-label={deleteStep === 'confirm' ? `Confirm delete "${spaceName}"` : `Delete "${spaceName}"`}
          style={deleteStyle}
        >
          {deleteStep === 'confirm' ? 'Tap again to delete' : 'Delete'}
        </button>
      )}
    </div>
  );
}
